/** Who is signed in, asked once.
 *
 * `GET /api/me` answers the whole account — user, identities, tokens,
 * installations, stats — so it is fetched once at the top of the tree and
 * shared, rather than by each screen that wants to know a display name. That
 * also settles a question every screen would otherwise have to answer for
 * itself: a `401` from `/api/me` is not a failure, it is the fact that nobody
 * is signed in, and it leaves `me` null with no error on the page.
 *
 * Signing out is a full navigation rather than a state update. The cookie is
 * gone the moment the request returns, and reloading is the only way to be
 * sure nothing rendered from the old session is still on screen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, getMe, signOut as postSignOut } from '@/lib/api';
import type { Me } from '@/lib/types';
import { Forbidden } from '@/components/ui/Forbidden';
import { NotFound } from '@/components/ui/NotFound';

export interface SessionState {
  /** Null once it is known that nobody is signed in. */
  me: Me | null;
  /** True until the first answer arrives. Guards against a signed-in visitor
   *  seeing the signed-out header for a frame. */
  loading: boolean;
  /** Set only when `/api/me` failed for a reason other than "not signed in". */
  error: Error | null;
  /** Re-read the account — after creating a token, renaming an installation,
   *  or changing the display name. */
  refresh: () => Promise<void>;
  /** Ends the session and lands on `to` — the public map unless told
   *  otherwise. "Switch account" asks for the sign-in page. */
  signOut: (to?: string) => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const account = await getMe();
      if (!live.current) return;
      setMe(account);
      setError(null);
    } catch (thrown) {
      if (!live.current) return;
      // Not signed in is an answer, not a problem.
      if (thrown instanceof ApiError && thrown.unauthenticated) {
        setMe(null);
        setError(null);
      } else {
        setMe(null);
        setError(thrown instanceof Error ? thrown : new Error(String(thrown)));
      }
    } finally {
      if (live.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async (to = '/tracks') => {
    try {
      await postSignOut();
    } finally {
      // Whether or not the request landed, the local view of the session is
      // over. A reload is the honest way to drop everything drawn from it.
      window.location.assign(to);
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({ me, loading, error, refresh, signOut }),
    [me, loading, error, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The account, as every screen sees it. */
export function useMe(): SessionState {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useMe was called outside SessionProvider');
  }
  return value;
}

/** True when the signed-in account owns the admin panel. */
export function useIsAdmin(): boolean {
  const { me } = useMe();
  return me?.user.role === 'admin';
}

/** Admin routes. Renders nothing until the account is known. After that a
 *  signed-out visitor is answered a 404 and not told the page exists, and a
 *  signed-in account that is not an admin is shown the black flag — a 403
 *  that names the role it holds and the one it would need.
 *
 *  The two are different on purpose. A stranger learns nothing from the 404.
 *  Somebody with an account is owed the reason, and hiding it bought nothing:
 *  `/api/admin` has always answered them `403 that is an administrator
 *  action`, and the route table ships in the bundle. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { me, loading } = useMe();
  if (loading) return null;
  if (!me) return <NotFound />;
  if (me.user.role !== 'admin') return <Forbidden />;
  return <>{children}</>;
}

/** Screens that are about the visitor's own driving. A signed-out visitor is
 *  sent to sign in and brought back to where they were aiming. */
export function RequireUser({ children }: { children: ReactNode }) {
  const { me, loading } = useMe();
  const location = useLocation();
  if (loading) return null;
  if (!me) {
    const back = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?redirect_to=${encodeURIComponent(back)}`} replace />;
  }
  return <>{children}</>;
}
